import { encryptData, decryptData } from "../settings/cryptoHelper"
// ==============================
// COMMON UTILS
// ==============================

export const isObjEmpty = (obj) => Object.keys(obj).length === 0

export const kFormatter = (num) => (num > 999 ? `${(num / 1000).toFixed(1)}k` : num)

export const htmlToString = (html) => html.replace(/<\/?[^>]+(>|$)/g, "")

const isToday = (date) => {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

export const formatDate = (
  value,
  formatting = { month: "short", day: "numeric", year: "numeric" }
) => {
  if (!value) return value
  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value))
}

export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value)
  let formatting = { month: "short", day: "numeric" }

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: "numeric", minute: "numeric" }
  }

  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value))
}

// ==============================
// 🔐 AUTH STORAGE (WITH CACHE)
// ==============================

const STORAGE_KEY = "userData"
// 🔥 CACHE (IMPORTANT)
let cachedUser = null
let isCacheLoaded = false

// 💾 Save (ALWAYS encrypted + update cache)
export const setUserData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY, encrypted)

  // ✅ update cache
  cachedUser = data
  isCacheLoaded = true
}

// 📤 Get user (SAFE + MIGRATION + CACHE)
export const getUserData = () => {
  // ✅ return cache if already loaded
  if (isCacheLoaded) return cachedUser

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      cachedUser = null
      isCacheLoaded = true
      return null
    }

    // ✅ Try decrypt
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedUser = decrypted
      isCacheLoaded = true
      return decrypted
    }

    // 🔁 Fallback: plain JSON → migrate
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY, encrypted)

    cachedUser = parsed
    isCacheLoaded = true
    return parsed

  } catch {
    cachedUser = null
    isCacheLoaded = true
    return null
  }
}

// ❌ Remove only user data + reset cache
export const removeUserData = () => {
  localStorage.removeItem(STORAGE_KEY)

  cachedUser = null
  isCacheLoaded = false
}

// ✅ Session check (FAST - no re-decrypt)
export const isUserLoggedInSession = () => {
  return !!getUserData()
}

// ✅ Get user safely (used in 200 components)
export const isUserLoggedIn = () => {
  return getUserData()
}

// ==============================
// JS SCRIPT DATA
// ==============================

// 🔥 CACHE (IMPORTANT)
const STORAGE_KEY_JS = "JsScriptData"
let cachedJS = null
let isCacheJSLoaded = false

// 💾 Save (ALWAYS encrypted + update cache)
export const setJSData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_JS, encrypted)

  // ✅ update cache
  cachedJS = data
  isCacheJSLoaded = true
}

// 📤 Get user (SAFE + MIGRATION + CACHE)
export const getJSData = () => {
  // ✅ return cache if already loaded
  if (isCacheJSLoaded) return cachedJS

  try {
    const stored = localStorage.getItem(STORAGE_KEY_JS)
    if (!stored) {
      cachedJS = null
      isCacheJSLoaded = true
      return null
    }

    // ✅ Try decrypt
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedJS = decrypted
      isCacheJSLoaded = true
      return decrypted
    }

    // 🔁 Fallback: plain JSON → migrate
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_JS, encrypted)

    cachedJS = parsed
    isCacheJSLoaded = true
    return parsed

  } catch {
    cachedJS = null
    isCacheJSLoaded = true
    return null
  }
}


// ==============================
// MERCHANT ONE DATA
// ==============================

// 🔥 CACHE (IMPORTANT)
const STORAGE_KEY_MO = "merchantOne"
let cachedMO = null
let isCacheMOLoaded = false

// 💾 Save (ALWAYS encrypted + update cache)
export const setMOData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_MO, encrypted)

  // ✅ update cache
  cachedMO = data
  isCacheMOLoaded = true
}

// 📤 Get user (SAFE + MIGRATION + CACHE)
export const getMOData = () => {
  // ✅ return cache if already loaded
  if (isCacheMOLoaded) return cachedMO

  try {
    const stored = localStorage.getItem(STORAGE_KEY_MO)
    if (!stored) {
      cachedMO = null
      isCacheMOLoaded = true
      return null
    }

    // ✅ Try decrypt
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedMO = decrypted
      isCacheMOLoaded = true
      return decrypted
    }

    // 🔁 Fallback: plain JSON → migrate
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_MO, encrypted)

    cachedMO = parsed
    isCacheMOLoaded = true
    return parsed

  } catch {
    cachedMO = null
    isCacheMOLoaded = true
    return null
  }
}

// ==============================
// AZURE B2C DATA
// ==============================

// 🔥 CACHE (IMPORTANT)
const STORAGE_KEY_AB2C = "azureAdB2C"
let cachedAB2C = null
let isCacheAB2CLoaded = false

// 💾 Save (ALWAYS encrypted + update cache)
export const setAB2CData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_AB2C, encrypted)

  // ✅ update cache
  cachedAB2C = data
  isCacheAB2CLoaded = true
}

// 📤 Get user (SAFE + MIGRATION + CACHE)
export const getAB2CData = () => {
  // ✅ return cache if already loaded
  if (isCacheAB2CLoaded) return cachedAB2C

  try {
    const stored = localStorage.getItem(STORAGE_KEY_AB2C)
    if (!stored) {
      cachedAB2C = null
      isCacheAB2CLoaded = true
      return null
    }

    // ✅ Try decrypt
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedAB2C = decrypted
      isCacheAB2CLoaded = true
      return decrypted
    }

    // 🔁 Fallback: plain JSON → migrate
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_AB2C, encrypted)

    cachedAB2C = parsed
    isCacheAB2CLoaded = true
    return parsed

  } catch {
    cachedAB2C = null
    isCacheAB2CLoaded = true
    return null
  }
}

// ==============================
// WorkOrderTypesAndCategories
// ==============================

// 🔥 CACHE (IMPORTANT)
const STORAGE_KEY_CAT = "workOrderTypesAndCategories"
let cachedCAT = null
let isCacheCATLoaded = false

// 💾 Save (ALWAYS encrypted + update cache)
export const setCATData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_CAT, encrypted)

  // ✅ update cache
  cachedCAT = data
  isCacheCATLoaded = true
}

// 📤 Get user (SAFE + MIGRATION + CACHE)
export const getCATData = () => {
  // ✅ return cache if already loaded
  if (isCacheCATLoaded) return cachedCAT

  try {
    const stored = localStorage.getItem(STORAGE_KEY_CAT)
    if (!stored) {
      cachedCAT = null
      isCacheCATLoaded = true
      return null
    }

    // ✅ Try decrypt
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedCAT = decrypted
      isCacheCATLoaded = true
      return decrypted
    }

    // 🔁 Fallback: plain JSON → migrate
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_CAT, encrypted)

    cachedCAT = parsed
    isCacheCATLoaded = true
    return parsed

  } catch {
    cachedCAT = null
    isCacheCATLoaded = true
    return null
  }
}

// ==============================
// WorkorderStages
// ==============================

// 🔥 CACHE (IMPORTANT)
const STORAGE_KEY_STG = "workorderStages"
let cachedSTG = null
let isCacheSTGLoaded = false

// 💾 Save (ALWAYS encrypted + update cache)
export const setSTGData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_STG, encrypted)

  // ✅ update cache
  cachedSTG = data
  isCacheSTGLoaded = true
}

// 📤 Get user (SAFE + MIGRATION + CACHE)
export const getSTGData = () => {
  // ✅ return cache if already loaded
  if (isCacheSTGLoaded) return cachedSTG

  try {
    const stored = localStorage.getItem(STORAGE_KEY_STG)
    if (!stored) {
      cachedSTG = null
      isCacheSTGLoaded = true
      return null
    }

    // ✅ Try decrypt
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedSTG = decrypted
      isCacheSTGLoaded = true
      return decrypted
    }

    // 🔁 Fallback: plain JSON → migrate
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_STG, encrypted)

    cachedSTG = parsed
    isCacheSTGLoaded = true
    return parsed

  } catch {
    cachedSTG = null
    isCacheSTGLoaded = true
    return null
  }
}

// ==============================
// ROUTING
// ==============================

export const getHomeRouteForLoggedInUser = (userRole) => {
  if (userRole === "admin") return '/home'
  if (userRole === "client") return "/access-control"
  return "/login"
}

// ==============================
// POPUP HANDLING
// ==============================

const documentPopupWindow = null
export default documentPopupWindow

let documentPopupWindowSingle = null

export const openDocumentPopup = (url) => {
  if (!documentPopupWindowSingle || documentPopupWindowSingle?.closed) {
    documentPopupWindowSingle = window.open(
      url,
      "documentPopup",
      "width=1000,height=700,scrollbars=yes,resizable=yes"
    )
  } else {
    documentPopupWindowSingle.location.href = url
    documentPopupWindowSingle?.focus()
  }
}

export const registerPopupCleanup = () => {
  const handleBeforeUnload = () => {
    if (documentPopupWindowSingle && !documentPopupWindowSingle?.closed) {
      documentPopupWindowSingle?.close()
    }
  }

  window.addEventListener("beforeunload", handleBeforeUnload)

  return () => {
    window.removeEventListener("beforeunload", handleBeforeUnload)
  }
}

// ==============================
// THEME
// ==============================

export const selectThemeColors = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#7367f01a",
    primary: "#7367f0",
    neutral10: "#7367f0",
    neutral20: "#ededed",
    neutral30: "#ededed"
  }
})

// ==============================
// LicenseGradeData
// ==============================

const STORAGE_KEY_LIC_GRADE = "LicenseGradeData"
let cachedLicGrade = null
let isCacheLicGradeLoaded = false

export const setLicenseGradeData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_LIC_GRADE, encrypted)
  cachedLicGrade = data
  isCacheLicGradeLoaded = true
}

export const getLicenseGradeData = () => {
  if (isCacheLicGradeLoaded) return cachedLicGrade
  try {
    const stored = localStorage.getItem(STORAGE_KEY_LIC_GRADE)
    if (!stored) {
      cachedLicGrade = null
      isCacheLicGradeLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedLicGrade = decrypted
      isCacheLicGradeLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_LIC_GRADE, encrypted)
    cachedLicGrade = parsed
    isCacheLicGradeLoaded = true
    return parsed
  } catch {
    cachedLicGrade = null
    isCacheLicGradeLoaded = true
    return null
  }
}

// ==============================
// LicenseIssueTypesData
// ==============================

const STORAGE_KEY_LIC_ISSUE = "LicenseIssueTypesData"
let cachedLicIssue = null
let isCacheLicIssueLoaded = false

export const setLicenseIssueTypesData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_LIC_ISSUE, encrypted)
  cachedLicIssue = data
  isCacheLicIssueLoaded = true
}

export const getLicenseIssueTypesData = () => {
  if (isCacheLicIssueLoaded) return cachedLicIssue
  try {
    const stored = localStorage.getItem(STORAGE_KEY_LIC_ISSUE)
    if (!stored) {
      cachedLicIssue = null
      isCacheLicIssueLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedLicIssue = decrypted
      isCacheLicIssueLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_LIC_ISSUE, encrypted)
    cachedLicIssue = parsed
    isCacheLicIssueLoaded = true
    return parsed
  } catch {
    cachedLicIssue = null
    isCacheLicIssueLoaded = true
    return null
  }
}

// ==============================
// LicenseLevelData
// ==============================

const STORAGE_KEY_LIC_LEVEL = "LicenseLevelData"
let cachedLicLevel = null
let isCacheLicLevelLoaded = false

export const setLicenseLevelData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_LIC_LEVEL, encrypted)
  cachedLicLevel = data
  isCacheLicLevelLoaded = true
}

export const getLicenseLevelData = () => {
  if (isCacheLicLevelLoaded) return cachedLicLevel
  try {
    const stored = localStorage.getItem(STORAGE_KEY_LIC_LEVEL)
    if (!stored) {
      cachedLicLevel = null
      isCacheLicLevelLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedLicLevel = decrypted
      isCacheLicLevelLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_LIC_LEVEL, encrypted)
    cachedLicLevel = parsed
    isCacheLicLevelLoaded = true
    return parsed
  } catch {
    cachedLicLevel = null
    isCacheLicLevelLoaded = true
    return null
  }
}

// ==============================
// SQCIssueTypesData
// ==============================

const STORAGE_KEY_SQC_ISSUE = "SQCIssueTypesData"
let cachedSQCIssue = null
let isCacheSQCIssueLoaded = false

export const setSQCIssueTypesData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_SQC_ISSUE, encrypted)
  cachedSQCIssue = data
  isCacheSQCIssueLoaded = true
}

export const getSQCIssueTypesData = () => {
  if (isCacheSQCIssueLoaded) return cachedSQCIssue
  try {
    const stored = localStorage.getItem(STORAGE_KEY_SQC_ISSUE)
    if (!stored) {
      cachedSQCIssue = null
      isCacheSQCIssueLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedSQCIssue = decrypted
      isCacheSQCIssueLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_SQC_ISSUE, encrypted)
    cachedSQCIssue = parsed
    isCacheSQCIssueLoaded = true
    return parsed
  } catch {
    cachedSQCIssue = null
    isCacheSQCIssueLoaded = true
    return null
  }
}

// ==============================
// SQCLevelData
// ==============================

const STORAGE_KEY_SQC_LEVEL = "SQCLevelData"
let cachedSQCLevel = null
let isCacheSQCLevelLoaded = false

export const setSQCLevelData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_SQC_LEVEL, encrypted)
  cachedSQCLevel = data
  isCacheSQCLevelLoaded = true
}

export const getSQCLevelData = () => {
  if (isCacheSQCLevelLoaded) return cachedSQCLevel
  try {
    const stored = localStorage.getItem(STORAGE_KEY_SQC_LEVEL)
    if (!stored) {
      cachedSQCLevel = null
      isCacheSQCLevelLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedSQCLevel = decrypted
      isCacheSQCLevelLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_SQC_LEVEL, encrypted)
    cachedSQCLevel = parsed
    isCacheSQCLevelLoaded = true
    return parsed
  } catch {
    cachedSQCLevel = null
    isCacheSQCLevelLoaded = true
    return null
  }
}

// ==============================
// VesselTypesData
// ==============================

const STORAGE_KEY_VESSEL = "VesselTypesData"
let cachedVessel = null
let isCacheVesselLoaded = false

export const setVesselTypesData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_VESSEL, encrypted)
  cachedVessel = data
  isCacheVesselLoaded = true
}

export const getVesselTypesData = () => {
  if (isCacheVesselLoaded) return cachedVessel
  try {
    const stored = localStorage.getItem(STORAGE_KEY_VESSEL)
    if (!stored) {
      cachedVessel = null
      isCacheVesselLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedVessel = decrypted
      isCacheVesselLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_VESSEL, encrypted)
    cachedVessel = parsed
    isCacheVesselLoaded = true
    return parsed
  } catch {
    cachedVessel = null
    isCacheVesselLoaded = true
    return null
  }
}

// ==============================
// countryStates
// ==============================

const STORAGE_KEY_COUNTRY = "countryStates"
let cachedCountry = null
let isCacheCountryLoaded = false

export const setCountryStatesData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_COUNTRY, encrypted)
  cachedCountry = data
  isCacheCountryLoaded = true
}

export const getCountryStatesData = () => {
  if (isCacheCountryLoaded) return cachedCountry
  try {
    const stored = localStorage.getItem(STORAGE_KEY_COUNTRY)
    if (!stored) {
      cachedCountry = null
      isCacheCountryLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedCountry = decrypted
      isCacheCountryLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_COUNTRY, encrypted)
    cachedCountry = parsed
    isCacheCountryLoaded = true
    return parsed
  } catch {
    cachedCountry = null
    isCacheCountryLoaded = true
    return null
  }
}

// ==============================
// FlagsData
// ==============================

const STORAGE_KEY_FLAGS = "FlagsData"
let cachedFlags = null
let isCacheFlagsLoaded = false

export const setFlagsData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_FLAGS, encrypted)
  cachedFlags = data
  isCacheFlagsLoaded = true
}

export const getFlagsData = () => {
  if (isCacheFlagsLoaded) return cachedFlags
  try {
    const stored = localStorage.getItem(STORAGE_KEY_FLAGS)
    if (!stored) {
      cachedFlags = null
      isCacheFlagsLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedFlags = decrypted
      isCacheFlagsLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_FLAGS, encrypted)
    cachedFlags = parsed
    isCacheFlagsLoaded = true
    return parsed
  } catch {
    cachedFlags = null
    isCacheFlagsLoaded = true
    return null
  }
}

// ==============================
// HairAndEyeColorData
// ==============================

const STORAGE_KEY_HAIR_EYE = "HairAndEyeColorData"
let cachedHairEye = null
let isCacheHairEyeLoaded = false

export const setHairAndEyeColorData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_HAIR_EYE, encrypted)
  cachedHairEye = data
  isCacheHairEyeLoaded = true
}

export const getHairAndEyeColorData = () => {
  if (isCacheHairEyeLoaded) return cachedHairEye
  try {
    const stored = localStorage.getItem(STORAGE_KEY_HAIR_EYE)
    if (!stored) {
      cachedHairEye = null
      isCacheHairEyeLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedHairEye = decrypted
      isCacheHairEyeLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_HAIR_EYE, encrypted)
    cachedHairEye = parsed
    isCacheHairEyeLoaded = true
    return parsed
  } catch {
    cachedHairEye = null
    isCacheHairEyeLoaded = true
    return null
  }
}

// ==============================
// BookIssueTypesData
// ==============================

const STORAGE_KEY_BOOK_ISSUE = "BookIssueTypesData"
let cachedBookIssue = null
let isCacheBookIssueLoaded = false

export const setBookIssueTypesData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_BOOK_ISSUE, encrypted)
  cachedBookIssue = data
  isCacheBookIssueLoaded = true
}

export const getBookIssueTypesData = () => {
  if (isCacheBookIssueLoaded) return cachedBookIssue
  try {
    const stored = localStorage.getItem(STORAGE_KEY_BOOK_ISSUE)
    if (!stored) {
      cachedBookIssue = null
      isCacheBookIssueLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedBookIssue = decrypted
      isCacheBookIssueLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_BOOK_ISSUE, encrypted)
    cachedBookIssue = parsed
    isCacheBookIssueLoaded = true
    return parsed
  } catch {
    cachedBookIssue = null
    isCacheBookIssueLoaded = true
    return null
  }
}

// ==============================
// BookTypesData
// ==============================

const STORAGE_KEY_BOOK_TYPE = "BookTypesData"
let cachedBookType = null
let isCacheBookTypeLoaded = false

export const setBookTypesData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_BOOK_TYPE, encrypted)
  cachedBookType = data
  isCacheBookTypeLoaded = true
}

export const getBookTypesData = () => {
  if (isCacheBookTypeLoaded) return cachedBookType
  try {
    const stored = localStorage.getItem(STORAGE_KEY_BOOK_TYPE)
    if (!stored) {
      cachedBookType = null
      isCacheBookTypeLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedBookType = decrypted
      isCacheBookTypeLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_BOOK_TYPE, encrypted)
    cachedBookType = parsed
    isCacheBookTypeLoaded = true
    return parsed
  } catch {
    cachedBookType = null
    isCacheBookTypeLoaded = true
    return null
  }
}

// ==============================
// CertificateCategoryData
// ==============================

const STORAGE_KEY_CERT_CAT = "CertificateCategoryData"
let cachedCertCat = null
let isCacheCertCatLoaded = false

export const setCertificateCategoryData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_CERT_CAT, encrypted)
  cachedCertCat = data
  isCacheCertCatLoaded = true
}

export const getCertificateCategoryData = () => {
  if (isCacheCertCatLoaded) return cachedCertCat
  try {
    const stored = localStorage.getItem(STORAGE_KEY_CERT_CAT)
    if (!stored) {
      cachedCertCat = null
      isCacheCertCatLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedCertCat = decrypted
      isCacheCertCatLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_CERT_CAT, encrypted)
    cachedCertCat = parsed
    isCacheCertCatLoaded = true
    return parsed
  } catch {
    cachedCertCat = null
    isCacheCertCatLoaded = true
    return null
  }
}

// ==============================
// CertificateTypesData
// ==============================

const STORAGE_KEY_CERT_TYPE = "CertificateTypesData"
let cachedCertType = null
let isCacheCertTypeLoaded = false

export const setCertificateTypesData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_CERT_TYPE, encrypted)
  cachedCertType = data
  isCacheCertTypeLoaded = true
}

export const getCertificateTypesData = () => {
  if (isCacheCertTypeLoaded) return cachedCertType
  try {
    const stored = localStorage.getItem(STORAGE_KEY_CERT_TYPE)
    if (!stored) {
      cachedCertType = null
      isCacheCertTypeLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedCertType = decrypted
      isCacheCertTypeLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_CERT_TYPE, encrypted)
    cachedCertType = parsed
    isCacheCertTypeLoaded = true
    return parsed
  } catch {
    cachedCertType = null
    isCacheCertTypeLoaded = true
    return null
  }
}

// ==============================
// CrewCapacity_FA_CAP
// ==============================

const STORAGE_KEY_CREW_CAP = "CrewCapacity_FA_CAP"
let cachedCrewCap = null
let isCacheCrewCapLoaded = false

export const setCrewCapacityFACAPData = (data, cacheKey) => {
  const encrypted = encryptData(data)
  localStorage.setItem(cacheKey, encrypted)
  cachedCrewCap = data
  isCacheCrewCapLoaded = true
}

export const getCrewCapacityFACAPData = (cacheKey) => {
  if (isCacheCrewCapLoaded) return cachedCrewCap
  try {
    const stored = localStorage.getItem(cacheKey)
    if (!stored) {
      cachedCrewCap = null
      isCacheCrewCapLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedCrewCap = decrypted
      isCacheCrewCapLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(cacheKey, encrypted)
    cachedCrewCap = parsed
    isCacheCrewCapLoaded = true
    return parsed
  } catch {
    cachedCrewCap = null
    isCacheCrewCapLoaded = true
    return null
  }
}

// ==============================
// CrewCapacity_FA_CAP
// ==============================
// OfficeData
// ==============================

const STORAGE_KEY_OFFICE = "OfficeData"
let cachedOffice = null
let isCacheOfficeLoaded = false

export const setOfficeData = (data) => {
  const encrypted = encryptData(data)
  localStorage.setItem(STORAGE_KEY_OFFICE, encrypted)
  cachedOffice = data
  isCacheOfficeLoaded = true
}

export const getOfficeData = () => {
  if (isCacheOfficeLoaded) return cachedOffice
  try {
    const stored = localStorage.getItem(STORAGE_KEY_OFFICE)
    if (!stored) {
      cachedOffice = null
      isCacheOfficeLoaded = true
      return null
    }
    const decrypted = decryptData(stored)
    if (decrypted) {
      cachedOffice = decrypted
      isCacheOfficeLoaded = true
      return decrypted
    }
    const parsed = JSON.parse(stored)
    const encrypted = encryptData(parsed)
    localStorage.setItem(STORAGE_KEY_OFFICE, encrypted)
    cachedOffice = parsed
    isCacheOfficeLoaded = true
    return parsed
  } catch {
    cachedOffice = null
    isCacheOfficeLoaded = true
    return null
  }
}

// ==============================
// OPTIONAL: Sync across tabs
// ==============================

window.addEventListener("storage", (e) => {
  if (e.key === STORAGE_KEY) {
    cachedUser = null
    isCacheLoaded = false
  }
  if (e.key === STORAGE_KEY_JS) {
    cachedJS = null
    isCacheJSLoaded = false
  }
  if (e.key === STORAGE_KEY_MO) {
    cachedMO = null
    isCacheMOLoaded = false
  }
  if (e.key === STORAGE_KEY_AB2C) {
    cachedAB2C = null
    isCacheAB2CLoaded = false
  }
  if (e.key === STORAGE_KEY_CAT) {
    cachedCAT = null
    isCacheCATLoaded = false
  }
  if (e.key === STORAGE_KEY_STG) {
    cachedSTG = null
    isCacheSTGLoaded = false
  }
  if (e.key === STORAGE_KEY_LIC_GRADE) {
    cachedLicGrade = null
    isCacheLicGradeLoaded = false
  }
  if (e.key === STORAGE_KEY_LIC_ISSUE) {
    cachedLicIssue = null
    isCacheLicIssueLoaded = false
  }
  if (e.key === STORAGE_KEY_LIC_LEVEL) {
    cachedLicLevel = null
    isCacheLicLevelLoaded = false
  }
  if (e.key === STORAGE_KEY_SQC_ISSUE) {
    cachedSQCIssue = null
    isCacheSQCIssueLoaded = false
  }
  if (e.key === STORAGE_KEY_SQC_LEVEL) {
    cachedSQCLevel = null
    isCacheSQCLevelLoaded = false
  }
  if (e.key === STORAGE_KEY_VESSEL) {
    cachedVessel = null
    isCacheVesselLoaded = false
  }
  if (e.key === STORAGE_KEY_COUNTRY) {
    cachedCountry = null
    isCacheCountryLoaded = false
  }
  if (e.key === STORAGE_KEY_FLAGS) {
    cachedFlags = null
    isCacheFlagsLoaded = false
  }
  if (e.key === STORAGE_KEY_HAIR_EYE) {
    cachedHairEye = null
    isCacheHairEyeLoaded = false
  }
  if (e.key === STORAGE_KEY_BOOK_ISSUE) {
    cachedBookIssue = null
    isCacheBookIssueLoaded = false
  }
  if (e.key === STORAGE_KEY_BOOK_TYPE) {
    cachedBookType = null
    isCacheBookTypeLoaded = false
  }
  if (e.key === STORAGE_KEY_CERT_CAT) {
    cachedCertCat = null
    isCacheCertCatLoaded = false
  }
  if (e.key === STORAGE_KEY_CERT_TYPE) {
    cachedCertType = null
    isCacheCertTypeLoaded = false
  }
  if (e.key === STORAGE_KEY_CREW_CAP) {
    cachedCrewCap = null
    isCacheCrewCapLoaded = false
  }
  if (e.key === STORAGE_KEY_OFFICE) {
    cachedOffice = null
    isCacheOfficeLoaded = false
  }
})